'use client';

import { FC, ReactNode, useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

import type { GalleryImage } from '@telialt/common-gallery/src';
import { useZoomControls } from '@telialt/common-gallery/src/ZoomProvider';
import { useBrowserModalControls } from '@telialt/common-hooks/src/useBrowserModalControls';
import { ImageFormat, ImageType, ProductImageData } from '@telialt/models/src/EshopModel';
import { isEmpty } from '@telialt/utils/src/helpers';

import { GalleryLoadingPlaceholder, StickyGalleryWrapper } from './GalleryContainer.styles';

const Gallery = dynamic(() => import('@telialt/common-gallery/src/Gallery').then((mod) => mod.Gallery), {
    ssr: false,
    loading: () => <GalleryLoadingPlaceholder />,
});

const GalleryLightBox = dynamic(
    () => import('@telialt/common-gallery/src/GalleryLightBox').then((mod) => mod.GalleryLightBox),
    { ssr: false },
);

const byImageFormatAndType = (format: `${ImageFormat}`, type: `${ImageType}` = ImageType.Gallery) => {
    return (image: ProductImageData) => image.format === format && image.imageType === type;
};

const toGalleryImage = (imageData: ProductImageData): GalleryImage => {
    return { src: imageData.url, altText: imageData.altText };
};

const ZOOM_MODAL = 'zoomModal';

type Props = {
    productImageData: ProductImageData[];
    isZoomEnabled?: boolean;
    renderBadges?: () => ReactNode;
    renderMarketingTag?: () => ReactNode;
    renderVideoly?: () => ReactNode;
    isFloatingNavButtonsEnabled?: boolean;
};

export const GalleryContainer: FC<Props> = ({
    productImageData = [],
    isZoomEnabled,
    renderBadges,
    renderMarketingTag,
    renderVideoly,
    isFloatingNavButtonsEnabled,
}) => {
    const [selectedId, setSelectedId] = useState<number>(0);
    const [scrollThumbs, setScrollThumbs] = useState<boolean>(false);

    const thumbs = productImageData.filter(byImageFormatAndType(ImageFormat.Thumbnail)).map(toGalleryImage);
    const images = productImageData.filter(byImageFormatAndType(ImageFormat.Product)).map(toGalleryImage);
    const bigImages = productImageData.filter(byImageFormatAndType(ImageFormat.Big)).map(toGalleryImage);
    const { setImageToZoomSrc } = useZoomControls();
    const selectedBigImage = bigImages[selectedId]?.src;

    useEffect(() => {
        if (isZoomEnabled) {
            setImageToZoomSrc(selectedBigImage);
        }
    }, [isZoomEnabled, selectedBigImage, setImageToZoomSrc]);

    const handleThumbnailSelect = (id: number) => {
        setSelectedId(id);
        setScrollThumbs(false);
    };

    const handleLightBoxSwipe = (id: number) => {
        setSelectedId(id);
        setScrollThumbs(true);
    };

    const { openModal, closeModal, isModalOpen } = useBrowserModalControls<Props>(ZOOM_MODAL);

    const hasImages = !isEmpty(images);

    return (
        <StickyGalleryWrapper>
            {isModalOpen && (
                <GalleryLightBox
                    images={bigImages}
                    thumbs={thumbs}
                    selectedId={selectedId}
                    onSwipe={handleLightBoxSwipe}
                    onThumbnailSelect={handleThumbnailSelect}
                    onClose={closeModal}
                    isModalOpen={isModalOpen}
                />
            )}
            <Gallery
                images={images}
                thumbs={thumbs}
                selectedId={selectedId}
                scrollThumbs={scrollThumbs}
                isZoomEnabled={isZoomEnabled}
                onThumbnailSelect={handleThumbnailSelect}
                onZoomImage={() => openModal()}
                renderMarketingTag={() => hasImages && renderMarketingTag != null && renderMarketingTag()}
                isModalOpen={isModalOpen}
                isFloatingNavButtonsEnabled={isFloatingNavButtonsEnabled}
            />
            {hasImages && renderBadges != null && renderBadges()}
            {renderVideoly != null && renderVideoly()}
        </StickyGalleryWrapper>
    );
};
